<template>
  <div id="app" class="container-fluid">
    <full_pnc_head :visit_id="visit_id" />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading postnatal visits...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>

      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-notes-medical"></i> Postnatal Visits</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Record Visit
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">
            No postnatal visits recorded yet for this patient.
          </div>
          <div v-else class="visit-list">
            <div class="visit-card" v-for="record in records" :key="record.id">
              <div class="visit-card-header">
                <div>
                  <strong>{{ record.visit_schedule || "Postnatal Visit" }}</strong>
                  <span v-if="record.days_post_delivery !== null" class="badge-site"
                    >Day {{ record.days_post_delivery }}</span
                  >
                </div>
                <div class="visit-actions">
                  <span class="visit-date">{{ formatDateTime(record.visit_date) }}</span>
                  <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="visit-body">
                <h5>Mother</h5>
                <div class="visit-row">
                  <span v-if="record.bp_systolic && record.bp_diastolic"
                    ><strong>BP:</strong> {{ record.bp_systolic }}/{{ record.bp_diastolic }}</span
                  >
                  <span v-if="record.pulse"><strong>Pulse:</strong> {{ record.pulse }}</span>
                  <span v-if="record.temperature"><strong>Temp:</strong> {{ record.temperature }}</span>
                  <span v-if="record.lochia"><strong>Lochia:</strong> {{ record.lochia }}</span>
                </div>
                <p v-if="record.mother_complaints" class="visit-text">
                  <strong>Complaints:</strong> {{ record.mother_complaints }}
                </p>

                <h5>Baby</h5>
                <div class="visit-row">
                  <span v-if="record.baby_weight_grams"><strong>Weight:</strong> {{ record.baby_weight_grams }} g</span>
                  <span v-if="record.feeding_status"><strong>Feeding:</strong> {{ record.feeding_status }}</span>
                  <span v-if="record.cord_status"><strong>Cord:</strong> {{ record.cord_status }}</span>
                </div>
                <div class="visit-flags">
                  <span v-if="record.jaundice_present" class="flag flag-danger">Jaundice</span>
                  <span v-if="record.immunization_up_to_date" class="flag flag-ok">Immunization up to date</span>
                </div>
                <p v-if="record.baby_danger_signs" class="visit-text">
                  <strong>Danger Signs:</strong> {{ record.baby_danger_signs }}
                </p>

                <p v-if="record.plan" class="visit-text"><strong>Plan:</strong> {{ record.plan }}</p>
                <p v-if="record.notes" class="visit-text"><strong>Notes:</strong> {{ record.notes }}</p>
                <p v-if="record.seen_by" class="visit-meta">Seen by {{ record.seen_by }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PncVisitModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_pnc_head from "./full_pnc_head.vue";
import PncVisitModal from "./PncVisitModal.vue";
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "PncVisitAssessment",
  components: { full_pnc_head, PncVisitModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`pnc_visits_records/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load postnatal visits";
        }
      } catch (err) {
        console.error("Error fetching PNC visits:", err);
        this.error = "An error occurred while loading postnatal visits. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this postnatal visit?")) return;
      try {
        const response = await generalApiRequest(`pnc_visits_records/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete postnatal visit");
        }
      } catch (error) {
        console.error("Error deleting PNC visit:", error);
        alert("Failed to delete postnatal visit. Please try again.");
      }
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #319795; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #319795; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #319795; }
.btn-edit-header { background: linear-gradient(135deg, #319795 0%, #285e61 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.visit-list { display: flex; flex-direction: column; gap: 14px; }
.visit-card { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.visit-card-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; flex-wrap: wrap; gap: 8px; }
.badge-site { margin-left: 8px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #e6fffa; color: #285e61; }
.visit-actions { display: flex; align-items: center; gap: 8px; }
.visit-date { font-size: 12px; color: #6c757d; }
.btn-icon { background: none; border: none; color: #319795; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
.visit-body { padding: 12px 14px; }
.visit-body h5 { font-size: 12px; text-transform: uppercase; color: #319795; margin: 10px 0 6px; }
.visit-body h5:first-child { margin-top: 0; }
.visit-row { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #495057; margin-bottom: 6px; }
.visit-flags { display: flex; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
.flag { background: #fff3cd; color: #856404; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.flag-danger { background: #f8d7da; color: #721c24; }
.flag-ok { background: #d4edda; color: #155724; }
.visit-text { font-size: 13px; color: #495057; margin: 4px 0; }
.visit-meta { font-size: 11px; color: #6c757d; font-style: italic; margin-top: 8px; }
</style>
